// @flow weak

import uuid from 'uuid';
import {ADD as NOTIFICATION} from './notification';
import {GET, PUT, POST} from '../helpers/request';
import {FILE_UPLOAD, ABORT_UPLOAD} from '../middleware/fileUploader';
import {STANDARD} from '../assortments';
import _ from 'lodash/fp';
import {sortByName} from './apartmentConfigView';

export const PROJECT_GET_REQUEST = '@projectView/PROJECT_GET_REQUEST';
export const PROJECT_GET_SUCCESS = '@projectView/PROJECT_GET_SUCCESS';
export const PROJECT_GET_FAILURE = '@projectView/PROJECT_GET_FAILURE';

export const PROJECT_CREATE_REQUEST = '@projectView/PROJECT_CREATE_REQUEST';
export const PROJECT_CREATE_SUCCESS = '@projectView/PROJECT_CREATE_SUCCESS';
export const PROJECT_CREATE_FAILURE = '@projectView/PROJECT_CREATE_FAILURE';

export const PROJECT_UPDATE_REQUEST = '@projectView/PROJECT_UPDATE_REQUEST';
export const PROJECT_UPDATE_SUCCESS = '@projectView/PROJECT_UPDATE_SUCCESS';
export const PROJECT_UPDATE_FAILURE = '@projectView/PROJECT_UPDATE_FAILURE';

export const PRODUCTS_REQUEST = '@projectView/PRODUCTS_REQUEST';
export const PRODUCTS_SUCCESS = '@projectView/PRODUCTS_SUCCESS';
export const PRODUCTS_FAILURE = '@projectView/PRODUCTS_FAILURE';

export const ROOM_TYPES_REQUEST = '@projectView/ROOM_TYPES_REQUEST';
export const ROOM_TYPES_SUCCESS = '@projectView/ROOM_TYPES_SUCCESS';
export const ROOM_TYPES_FAILURE = '@projectView/ROOM_TYPES_FAILURE';

export const ADD_ROOM_TYPE_IMAGE = '@projectView/ADD_ROOM_TYPE_IMAGE';
export const REMOVE_ROOM_TYPE_IMAGE = '@projectView/REMOVE_ROOM_TYPE_IMAGE';
export const UPLOAD_ROOM_TYPE_IMAGE = '@projectView/UPLOAD_ROOM_TYPE_IMAGE';

export const ADD_PROJECT_FILE = '@projectView/ADD_PROJECT_FILE';
export const REMOVE_PROJECT_FILE = '@projectView/REMOVE_PROJECT_FILE';
export const UPLOAD_PROJECT_FILE = '@projectView/UPLOAD_PROJECT_FILE';

export const RESET = '@projectView/RESET';
export const ABORT_FILE_UPLOAD = '@projectView/ABORT_FILE_UPLOAD';
export const FILTER_APARTMENTS = '@projectView/FILTER_APARTMENTS';
export const SET_ACTIVE_ROOM_TYPE = '@projectView/SET_ACTIVE_ROOM_TYPE';
export const CLEAR = '@projectView/CLEAR';
export const SORT = '@projectView/SORT';

export const ACTIVATION_REQUEST = '@projectView/ACTIVATION_REQUEST';
export const ACTIVATION_SUCCESS = '@projectView/ACTIVATION_SUCCESS';
export const ACTIVATION_FAILURE = '@projectView/ACTIVATION_FAILURE';

export const LINK_COPY_MESSAGE = '@projectView/LINK_COPY_MESSAGE';

const activationRequest = () => ({
  type: ACTIVATION_REQUEST,
});

const activationSuccess = () => ({
  type: ACTIVATION_SUCCESS,
  [NOTIFICATION]: {
    message: 'Aktiveringslänk(ar) skickad(e)',
  },
});

const activationFailure = error => ({
  type: ACTIVATION_FAILURE,
  error,
});

const projectRequest = () => ({
  type: PROJECT_GET_REQUEST,
});

const projectSuccess = (project, apartmentConfigurations, apartments) => ({
  type: PROJECT_GET_SUCCESS,
  project,
  apartmentConfigurations,
  apartments,
});

const projectFailure = error => ({
  type: PROJECT_GET_FAILURE,
  error,
});

const projectCreateRequest = () => ({
  type: PROJECT_CREATE_REQUEST,
});

const projectCreateSuccess = project => ({
  type: PROJECT_CREATE_SUCCESS,
  project,
  [NOTIFICATION]: {
    message: `Projekt "${project.name}" skapad`,
  },
});

const projectCreateFailure = error => ({
  type: PROJECT_CREATE_FAILURE,
  error,
});

const projectUpdateRequest = () => ({
  type: PROJECT_UPDATE_REQUEST,
});

const projectUpdateSuccess = project => ({
  type: PROJECT_UPDATE_SUCCESS,
  project,
  [NOTIFICATION]: {
    message: 'Projekt uppdaterat',
  },
});

const projectUpdateFailure = error => ({
  type: PROJECT_UPDATE_FAILURE,
  error,
});

const productsRequest = () => ({
  type: PRODUCTS_REQUEST,
});

const productsSuccess = products => ({
  type: PRODUCTS_SUCCESS,
  products,
});

const productsFailure = error => ({
  type: PRODUCTS_FAILURE,
  error,
});

const roomTypesRequest = () => ({
  type: ROOM_TYPES_REQUEST,
});

const roomTypesSuccess = roomTypes => ({
  type: ROOM_TYPES_SUCCESS,
  roomTypes,
});

const roomTypesFailure = error => ({
  type: ROOM_TYPES_FAILURE,
  error,
});

const addProjectFile = name => (tempId, metadata) => ({
  type: ADD_PROJECT_FILE,
  name,
  tempId,
  metadata,
});

const addRoomTypeImage = roomTypeId => (tempId, metadata) => ({
  type: ADD_ROOM_TYPE_IMAGE,
  roomTypeId,
  metadata,
});

const linkCopied = () => ({
  type: LINK_COPY_MESSAGE,
  [NOTIFICATION]: {
    message: 'Länk kopierad!',
  },
});

export const uploadProjectFile = (name, file) => ({
  type: UPLOAD_PROJECT_FILE,
  tempId: uuid.v4(),
  name,
  [FILE_UPLOAD]: {
    file,
    success: addProjectFile(name),
  },
});

export const removeProjectFile = name => ({
  type: REMOVE_PROJECT_FILE,
  name,
});

export const uploadRoomTypeImage = (roomTypeId, file) => ({
  type: UPLOAD_ROOM_TYPE_IMAGE,
  tempId: uuid.v4(),
  roomTypeId,
  [FILE_UPLOAD]: {
    file,
    success: addRoomTypeImage(roomTypeId),
  },
});

export const removeRoomTypeImage = fileId => ({
  type: REMOVE_ROOM_TYPE_IMAGE,
  fileId,
});

export const abortFileUpload = tempId => ({
  type: ABORT_FILE_UPLOAD,
  tempId,
  [ABORT_UPLOAD]: true,
});

export const filterApartments = query => ({
  type: FILTER_APARTMENTS,
  query,
});

export const setActiveRoomType = roomType => ({
  type: SET_ACTIVE_ROOM_TYPE,
  roomType,
});

export const abortAllUploads = (uploads, roomTypeImageUploads) => dispatch => {
  _.forEach(u => dispatch(abortFileUpload(u)), uploads);
  _.forEach(u => dispatch(abortFileUpload(u)), _.flatten(_.values(roomTypeImageUploads)));
};

export const reset = () => ({
  type: RESET,
});

export const clear = () => ({
  type: CLEAR,
});

export const fetchProject = id => async dispatch => {
  dispatch(projectRequest());
  try {
    const project = await GET({endpoint: `/projects/${id}`}, dispatch);
    const apartmentConfigurations = sortByName(await GET({endpoint: `/projects/${id}/apartmentconfigs`}, dispatch));
    const apartments = await GET({endpoint: `/projects/${id}/apartments`}, dispatch);
    dispatch(projectSuccess(project, apartmentConfigurations, apartments));
  } catch (err) {
    dispatch(projectFailure(err));
  }
};

export const createProject = body => async dispatch => {
  dispatch(projectCreateRequest());
  try {
    const project = await POST({
      endpoint: '/projects',
      body,
    }, dispatch);
    dispatch(projectCreateSuccess(project));
  } catch (err) {
    dispatch(projectCreateFailure(err));
  }
};

export const updateProject = (id, body) => async dispatch => {
  dispatch(projectUpdateRequest());
  try {
    const project = await PUT({
      endpoint: `/projects/${id}`,
      body,
    }, dispatch);
    dispatch(projectUpdateSuccess(project));
  } catch (err) {
    dispatch(projectUpdateFailure(err));
  }
};

export const fetchProducts = () => async dispatch => {
  dispatch(productsRequest());
  try {
    const products = await GET({endpoint: `/products?assortment=${STANDARD}`}, dispatch);
    dispatch(productsSuccess(products));
  } catch (err) {
    dispatch(productsFailure(err));
  }
};

export const fetchRoomTypes = () => async dispatch => {
  dispatch(roomTypesRequest());
  try {
    const roomTypes = sortByName(await GET({endpoint: '/roomtypes'}, dispatch));
    dispatch(roomTypesSuccess(roomTypes));
  } catch (err) {
    dispatch(roomTypesFailure(err));
  }
};

export const sendActivationLinks = (projectId: number) => async dispatch => {
  dispatch(activationRequest());
  try {
    await POST({
      endpoint: `/projects/${projectId}/activate`,
      expectBody: false,
    }, dispatch);
    dispatch(activationSuccess());
  } catch (err) {
    dispatch(activationFailure(err));
  }
};

export const notifyLinkCopied = () => async dispatch => {
  dispatch(linkCopied());
};

export const sortItems = sorter => ({
  type: SORT,
  sorter,
});



// WEBPACK FOOTER //
// ./src/actions/projectView.js